import { useMemo } from 'react';
import { CardBadgeSectionProps, CardBadgeItem } from '@/shadcn/components/card-overlay';

export interface UseCardBadgeSectionOptions {
  /** Array of custom badge items */
  badges?: CardBadgeItem[];
  /** Show boost eligible badge */
  showBoostEligible?: boolean;
  /** Whether content is boost eligible */
  isBoostEligible?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Custom children badges */
  children?: React.ReactNode;
}

export interface UseCardBadgeSectionResult {
  /** Props for CardBadgeSection component */
  cardBadgeSectionProps: CardBadgeSectionProps;
  /** Whether any badges should be displayed */
  hasBadges: boolean;
}

/**
 * useCardBadgeSection
 * -------------------
 * Hook to build props for CardBadgeSection component.
 * Provides common badge configurations and validation logic.
 *
 * @param options - Configuration options for badge section
 * @returns Object with cardBadgeSectionProps and hasBadges flag
 */
export const useCardBadgeSection = (
  options: UseCardBadgeSectionOptions = {}
): UseCardBadgeSectionResult => {
  const {
    badges = [],
    showBoostEligible = true,
    isBoostEligible = false,
    className,
    children,
  } = options;

  const cardBadgeSectionProps = useMemo<CardBadgeSectionProps>(() => ({
    badges,
    showBoostEligible,
    isBoostEligible,
    className,
    children,
  }), [badges, showBoostEligible, isBoostEligible, className, children]);

  const hasBadges = useMemo(() => {
    return (
      (showBoostEligible && isBoostEligible) ||
      badges.length > 0 ||
      Boolean(children)
    );
  }, [showBoostEligible, isBoostEligible, badges.length, children]);

  return {
    cardBadgeSectionProps,
    hasBadges,
  };
};

/**
 * useContentPreviewBadges
 * -----------------------
 * Preset hook for content preview cards with boost eligible badge.
 */
export const useContentPreviewBadges = (
  isBoostEligible: boolean = false,
  customBadges: CardBadgeItem[] = []
): UseCardBadgeSectionResult => {
  return useCardBadgeSection({
    badges: customBadges,
    showBoostEligible: true,
    isBoostEligible,
  });
};

/**
 * useSimilarCreatorsBadges
 * ------------------------
 * Preset hook for similar creators cards with boost eligible badge.
 */
export const useSimilarCreatorsBadges = (
  isBoostEligible: boolean = false
): UseCardBadgeSectionResult => {
  return useCardBadgeSection({
    showBoostEligible: true,
    isBoostEligible,
  });
};

/**
 * useCustomBadges
 * ---------------
 * Hook for cards with only custom badges (no boost eligible).
 */
export const useCustomBadges = (
  badges: CardBadgeItem[] = []
): UseCardBadgeSectionResult => {
  return useCardBadgeSection({
    badges,
    showBoostEligible: false,
    isBoostEligible: false,
  });
};
